import {
  CHANNELS,
  INSTALLMENT_STATUSES,
  PRODUCT_FAMILIES,
  scenarioInputSchema,
  type Channel,
  type ConditionInput,
  type Millimes,
} from '@beta-assur/shared';
import { useNavigate, useParams } from '@tanstack/react-router';
import { ArrowDown, ArrowUp, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ConfirmButton } from '../components/confirm';
import { Facts, Field, PageHeader } from '../components/page';
import { ActionError, Empty, QueryView } from '../components/states';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox, Input, Select } from '../components/ui/input';
import { Table, TBody, TD, TH, THead, TR } from '../components/ui/table';
import { useAuth } from '../lib/auth';
import { day, instant, money } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { useApiMutation, useApiQuery } from '../lib/query';
import { useTenant } from '../lib/referentials';
import type { Scenario, ScenarioPreview, ScenarioStep, TemplateList } from '../lib/types';

// Éditeur de scénario (BA-024, spec 08 §4) : étapes, secours, condition simple, aperçu.
// Décalage compté depuis l'échéance ou le préavis (BA-079), canal préféré d'abord (BA-080).

interface StepDraft {
  kind: 'message' | 'escalation';
  /** Saisie brute : « - » seul est un état intermédiaire valide pendant la frappe. */
  offsetDays: string;
  offsetFrom: 'due' | 'notice';
  /** Canal préféré de l'assuré essayé avant le canal principal. */
  preferred: boolean;
  channelPrimary: Channel;
  templateId: string;
  channelFallback: Channel | '';
  fallbackTemplateId: string;
  condition: ConditionInput | null;
  label: string;
}

/** Condition « statut dans la liste » éditable ; toute autre condition est conservée telle quelle. */
function statusList(condition: ConditionInput | null): string[] | null {
  if (
    condition &&
    'field' in condition &&
    condition.field === 'status' &&
    condition.op === 'in' &&
    Array.isArray(condition.value)
  ) {
    return condition.value.map(String);
  }
  return condition === null ? [] : null;
}

function toDraft(step: ScenarioStep): StepDraft {
  return {
    kind: step.kind,
    offsetDays: String(step.offsetDays),
    offsetFrom: step.offsetFrom,
    preferred: step.channelMode === 'preferred',
    channelPrimary: step.channelPrimary ?? 'sms',
    templateId: step.templateId ?? '',
    channelFallback: step.channelFallback ?? '',
    fallbackTemplateId: step.fallbackTemplateId ?? '',
    condition: (step.condition as ConditionInput | null) ?? null,
    label: step.label ?? '',
  };
}

function toInput(step: StepDraft) {
  const common = {
    offsetDays: /^-?\d+$/.test(step.offsetDays.trim()) ? Number(step.offsetDays) : Number.NaN,
    offsetFrom: step.offsetFrom,
    condition: step.condition,
    label: step.label.trim() || null,
  };
  return step.kind === 'escalation'
    ? { kind: 'escalation' as const, ...common }
    : {
        kind: 'message' as const,
        ...common,
        channelMode: step.preferred ? ('preferred' as const) : ('fixed' as const),
        channelPrimary: step.channelPrimary,
        templateId: step.templateId,
        channelFallback: step.channelFallback || null,
        fallbackTemplateId: step.channelFallback ? step.fallbackTemplateId || null : null,
      };
}

function StepRow({
  step,
  index,
  count,
  templates,
  readOnly,
  onChange,
  onMove,
  onRemove,
}: {
  step: StepDraft;
  index: number;
  count: number;
  templates: TemplateList['items'];
  readOnly: boolean;
  onChange: (patch: Partial<StepDraft>) => void;
  onMove: (delta: number) => void;
  onRemove: () => void;
}) {
  const t = useI18n().t;
  const statuses = statusList(step.condition);
  const options = (channel: Channel | '') => templates.filter((tpl) => tpl.channel === channel);
  const n = index + 1;

  return (
    <TR>
      <TD>
        <Badge tone={step.kind === 'escalation' ? 'danger' : 'info'}>
          {t.scenarios.kinds[step.kind]}
        </Badge>
      </TD>
      <TD>
        <div className="flex flex-col gap-1">
          <Input
            type="text"
            inputMode="numeric"
            aria-label={`${t.scenarios.offset} ${n}`}
            className="w-20"
            value={step.offsetDays}
            disabled={readOnly}
            onChange={(e) => onChange({ offsetDays: e.target.value })}
          />
          <Select
            aria-label={`${t.scenarios.offsetFrom} ${n}`}
            className="w-32 text-xs"
            value={step.offsetFrom}
            disabled={readOnly}
            onChange={(e) => onChange({ offsetFrom: e.target.value as StepDraft['offsetFrom'] })}
          >
            <option value="due">{t.scenarios.fromDue}</option>
            <option value="notice">{t.scenarios.fromNotice}</option>
          </Select>
        </div>
      </TD>
      <TD>
        {step.kind === 'message' ? (
          <div className="flex min-w-56 flex-col gap-1">
            <label className="flex items-center gap-1 text-xs">
              <Checkbox
                checked={step.preferred}
                disabled={readOnly}
                onChange={(e) => onChange({ preferred: e.target.checked })}
              />
              {t.scenarios.preferredFirst}
            </label>
            <div className="flex gap-1">
              <Select
                aria-label={`${t.scenarios.channel} ${n}`}
                className="w-28"
                value={step.channelPrimary}
                disabled={readOnly}
                onChange={(e) =>
                  onChange({ channelPrimary: e.target.value as Channel, templateId: '' })
                }
              >
                {CHANNELS.map((c) => (
                  <option key={c} value={c}>
                    {t.channels[c]}
                  </option>
                ))}
              </Select>
              <Select
                aria-label={`${t.scenarios.template} ${n}`}
                value={step.templateId}
                disabled={readOnly}
                onChange={(e) => onChange({ templateId: e.target.value })}
              >
                <option value="">-</option>
                {options(step.channelPrimary).map((tpl) => (
                  <option key={tpl.id} value={tpl.id}>
                    {tpl.name} ({tpl.language})
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex gap-1">
              <Select
                aria-label={`${t.scenarios.fallback} ${n}`}
                className="w-28"
                value={step.channelFallback}
                disabled={readOnly}
                onChange={(e) =>
                  onChange({
                    channelFallback: e.target.value as Channel | '',
                    fallbackTemplateId: '',
                  })
                }
              >
                <option value="">{t.scenarios.noFallback}</option>
                {CHANNELS.filter((c) => c !== step.channelPrimary).map((c) => (
                  <option key={c} value={c}>
                    {t.channels[c]}
                  </option>
                ))}
              </Select>
              {step.channelFallback ? (
                <Select
                  aria-label={`${t.scenarios.fallbackTemplate} ${n}`}
                  value={step.fallbackTemplateId}
                  disabled={readOnly}
                  onChange={(e) => onChange({ fallbackTemplateId: e.target.value })}
                >
                  <option value="">-</option>
                  {options(step.channelFallback).map((tpl) => (
                    <option key={tpl.id} value={tpl.id}>
                      {tpl.name} ({tpl.language})
                    </option>
                  ))}
                </Select>
              ) : null}
            </div>
          </div>
        ) : (
          '-'
        )}
      </TD>
      <TD>
        {statuses === null ? (
          <code className="text-xs">{JSON.stringify(step.condition)}</code>
        ) : (
          <fieldset className="flex flex-wrap gap-x-3 gap-y-1 text-xs" disabled={readOnly}>
            <legend className="sr-only">
              {t.scenarios.conditionStatus} ({n})
            </legend>
            {(['due', 'overdue', 'partial'] as const).map((s) => (
              <label key={s} className="flex items-center gap-1">
                <Checkbox
                  checked={statuses.includes(s)}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? [...statuses, s]
                      : statuses.filter((x) => x !== s);
                    const ordered = INSTALLMENT_STATUSES.filter((x) => next.includes(x));
                    onChange({
                      condition: ordered.length
                        ? { field: 'status', op: 'in', value: ordered }
                        : null,
                    });
                  }}
                />
                {t.status[s]}
              </label>
            ))}
            {statuses.length === 0 ? (
              <span className="text-muted-foreground">({t.scenarios.noCondition})</span>
            ) : null}
          </fieldset>
        )}
      </TD>
      <TD>
        <Input
          aria-label={`${t.scenarios.label} ${n}`}
          value={step.label}
          disabled={readOnly}
          onChange={(e) => onChange({ label: e.target.value })}
        />
      </TD>
      <TD>
        {readOnly ? null : (
          <div className="flex gap-1">
            <Button
              size="icon"
              variant="ghost"
              aria-label={`${t.scenarios.moveUp} ${n}`}
              disabled={index === 0}
              onClick={() => onMove(-1)}
            >
              <ArrowUp className="h-4 w-4" aria-hidden />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              aria-label={`${t.scenarios.moveDown} ${n}`}
              disabled={index === count - 1}
              onClick={() => onMove(1)}
            >
              <ArrowDown className="h-4 w-4" aria-hidden />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              aria-label={`${t.scenarios.removeStep} ${n}`}
              disabled={count === 1}
              onClick={onRemove}
            >
              <Trash2 className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        )}
      </TD>
    </TR>
  );
}

interface SimulationResult {
  installments: number;
  messages: number;
  escalations: number;
  notStarted: number;
  estimatedCostMillimes: Millimes;
  calendar: { date: string; total: number; byChannel: Record<string, number> }[];
}

/** Simulation sur les échéances ouvertes d'une période (BA-053) : volumes, coût, calendrier. */
function Simulation({ scenarioId }: { scenarioId: string }) {
  const { t, language } = useI18n();
  const [range, setRange] = useState({ dueFrom: '', dueTo: '' });
  const [result, setResult] = useState<SimulationResult | null>(null);
  const run = useApiMutation<Record<string, string>, SimulationResult>(
    'POST',
    `/v1/scenarios/${scenarioId}/simulate`,
    { onSuccess: setResult },
  );
  const body = Object.fromEntries(Object.entries(range).filter(([, v]) => v));
  const peak = Math.max(1, ...(result?.calendar ?? []).map((d) => d.total));
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.scenarios.simulation}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <Field label={t.common.from}>
            <Input
              type="date"
              value={range.dueFrom}
              onChange={(e) => setRange((r) => ({ ...r, dueFrom: e.target.value }))}
            />
          </Field>
          <Field label={t.common.to}>
            <Input
              type="date"
              value={range.dueTo}
              onChange={(e) => setRange((r) => ({ ...r, dueTo: e.target.value }))}
            />
          </Field>
          <Button variant="outline" disabled={run.isPending} onClick={() => run.mutate(body)}>
            {t.scenarios.simulate}
          </Button>
        </div>
        <ActionError error={run.error} />
        {result ? (
          <>
            <Facts
              items={[
                [t.scenarios.simInstallments, String(result.installments)],
                [t.scenarios.simMessages, String(result.messages)],
                [t.scenarios.simEscalations, String(result.escalations)],
                [t.scenarios.simNotStarted, String(result.notStarted)],
                [t.scenarios.simCost, money(result.estimatedCostMillimes, language)],
              ]}
            />
            {result.calendar.length ? (
              <ol className="flex flex-col gap-1" aria-label={t.scenarios.simCalendar}>
                {result.calendar.map((d) => (
                  <li key={d.date} className="flex items-center gap-2 text-xs">
                    <span className="w-24 shrink-0 tabular-nums">{day(d.date, language)}</span>
                    <span
                      className="h-2 rounded-full bg-primary"
                      style={{ width: `${(d.total / peak) * 60}%` }}
                      aria-hidden
                    />
                    <span className="tabular-nums">{d.total}</span>
                  </li>
                ))}
              </ol>
            ) : null}
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}

function Preview({ scenarioId }: { scenarioId: string }) {
  const { t, language } = useI18n();
  const tz = useTenant().data?.timezone;
  const [dueDate, setDueDate] = useState('');
  const preview = useApiQuery<ScenarioPreview>(
    dueDate ? `/v1/scenarios/${scenarioId}/preview` : null,
    { dueDate },
  );
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.scenarios.preview}</CardTitle>
        <Field label={t.scenarios.previewDueDate} className="flex-row items-center gap-2">
          <Input
            type="date"
            className="w-44"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />
        </Field>
      </CardHeader>
      {!dueDate ? null : (
        <QueryView query={preview}>
          {(p) =>
            p.steps.length === 0 ? (
              <Empty />
            ) : (
              <ol className="divide-y divide-border">
                {p.steps.map((s) => (
                  <li
                    key={s.orderNo}
                    className="flex flex-wrap justify-between gap-2 px-4 py-2 text-sm"
                  >
                    <span>
                      {s.orderNo}. {s.label ?? t.scenarios.kinds[s.kind]}{' '}
                      {s.channel ? `(${t.channels[s.channel]})` : ''}
                    </span>
                    <span className="text-muted-foreground">
                      {day(s.date, language)} · {t.scenarios.plannedAt}{' '}
                      {instant(s.plannedAt, tz, language)}
                    </span>
                  </li>
                ))}
              </ol>
            )
          }
        </QueryView>
      )}
    </Card>
  );
}

function Editor({ scenario }: { scenario: Scenario }) {
  const t = useI18n().t;
  const auth = useAuth();
  const navigate = useNavigate();
  const readOnly = !auth.can('administer') || scenario.status === 'archived';
  const templates = useApiQuery<TemplateList>('/v1/templates');
  const [meta, setMeta] = useState({
    name: scenario.name,
    productFamily: scenario.productFamily ?? '',
    stopOnPayment: scenario.stopOnPayment,
    pauseOnPromiseDays: scenario.pauseOnPromiseDays,
  });
  const [steps, setSteps] = useState<StepDraft[]>(scenario.steps.map(toDraft));
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [applyToRunning, setApplyToRunning] = useState(false);
  useEffect(() => {
    setSteps(scenario.steps.map(toDraft));
  }, [scenario]);

  const save = useApiMutation<unknown, Scenario>('PUT', `/v1/scenarios/${scenario.id}`, {
    invalidate: ['/v1/scenarios'],
    onSuccess: () => setSaved(true),
  });
  const publish = useApiMutation<unknown>('POST', `/v1/scenarios/${scenario.id}/publish`, {
    invalidate: ['/v1/scenarios'],
  });
  const duplicate = useApiMutation<undefined, Scenario>(
    'POST',
    `/v1/scenarios/${scenario.id}/duplicate`,
    {
      invalidate: ['/v1/scenarios'],
      onSuccess: (copy) => void navigate({ to: '/scenarios/$id', params: { id: copy.id } }),
    },
  );

  const update = (index: number, patch: Partial<StepDraft>) => {
    setSaved(false);
    setSteps((list) => list.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  };
  const move = (index: number, delta: number) =>
    setSteps((list) => {
      const next = [...list];
      const [item] = next.splice(index, 1);
      next.splice(index + delta, 0, item as StepDraft);
      return next;
    });
  const add = (kind: StepDraft['kind']) =>
    setSteps((list) => [
      ...list,
      {
        kind,
        offsetDays: String(Number(list.at(-1)?.offsetDays ?? 0) + 1),
        offsetFrom: list.at(-1)?.offsetFrom ?? 'due',
        preferred: kind === 'message',
        channelPrimary: 'sms',
        templateId: '',
        channelFallback: '',
        fallbackTemplateId: '',
        condition: null,
        label: '',
      },
    ]);

  function submit() {
    setSaved(false);
    const parsed = scenarioInputSchema.safeParse({
      name: meta.name,
      productFamily: meta.productFamily || null,
      stopOnPayment: meta.stopOnPayment,
      pauseOnPromiseDays: meta.pauseOnPromiseDays,
      steps: steps.map(toInput),
    });
    if (!parsed.success) {
      setError(parsed.error.issues.map((i) => i.message).join(' · '));
      return;
    }
    setError(null);
    save.mutate(parsed.data);
  }

  return (
    <>
      <PageHeader
        title={scenario.name}
        subtitle={
          <span className="flex flex-wrap gap-2">
            <Badge>{t.scenarios.statuses[scenario.status]}</Badge>
            <span>
              {t.scenarios.version} {scenario.version}
              {scenario.publishedVersion
                ? ` (${t.scenarios.published} v${scenario.publishedVersion})`
                : ''}
            </span>
          </span>
        }
        actions={
          readOnly ? null : (
            <>
              <Button size="sm" variant="outline" onClick={() => duplicate.mutate(undefined)}>
                {t.scenarios.duplicate}
              </Button>
              <ConfirmButton
                label={t.scenarios.archive}
                title={t.scenarios.archive}
                message={t.scenarios.archiveConfirm}
                variant="destructive"
                onConfirm={async () => {
                  await auth.api.request('DELETE', `/v1/scenarios/${scenario.id}`);
                  await navigate({ to: '/scenarios' });
                }}
              />
              <ConfirmButton
                label={t.scenarios.publish}
                title={t.scenarios.publish}
                message={t.scenarios.publishConfirm}
                variant="default"
                disabled={!scenario.hasDraft && scenario.status === 'published'}
                onConfirm={() => publish.mutateAsync(applyToRunning ? { applyToRunning } : {})}
              >
                {scenario.publishedVersion ? (
                  <label className="flex items-start gap-2 text-sm">
                    <Checkbox
                      checked={applyToRunning}
                      onChange={(e) => setApplyToRunning(e.target.checked)}
                    />
                    {t.scenarios.applyToRunning}
                  </label>
                ) : null}
              </ConfirmButton>
            </>
          )
        }
      />
      <div className="flex flex-col gap-4">
        <Card>
          <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Field label={t.scenarios.name}>
              <Input
                value={meta.name}
                disabled={readOnly}
                onChange={(e) => setMeta((m) => ({ ...m, name: e.target.value }))}
              />
            </Field>
            <Field label={t.scenarios.family}>
              <Select
                value={meta.productFamily}
                disabled={readOnly}
                onChange={(e) => setMeta((m) => ({ ...m, productFamily: e.target.value }))}
              >
                <option value="">{t.scenarios.anyFamily}</option>
                {PRODUCT_FAMILIES.map((f) => (
                  <option key={f} value={f}>
                    {t.families[f]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t.scenarios.pauseOnPromise}>
              <Input
                type="number"
                min={0}
                max={60}
                value={meta.pauseOnPromiseDays}
                disabled={readOnly}
                onChange={(e) =>
                  setMeta((m) => ({ ...m, pauseOnPromiseDays: Number(e.target.value) }))
                }
              />
            </Field>
            <label className="flex items-center gap-2 self-end pb-2 text-sm">
              <Checkbox
                checked={meta.stopOnPayment}
                disabled={readOnly}
                onChange={(e) => setMeta((m) => ({ ...m, stopOnPayment: e.target.checked }))}
              />
              {t.scenarios.stopOnPayment}
            </label>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>{t.scenarios.steps}</CardTitle>
            {readOnly ? null : (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => add('message')}>
                  {t.scenarios.addMessage}
                </Button>
                <Button size="sm" variant="outline" onClick={() => add('escalation')}>
                  {t.scenarios.addEscalation}
                </Button>
              </div>
            )}
          </CardHeader>
          <Table>
            <THead>
              <tr>
                <TH>{t.scenarios.kind}</TH>
                <TH>{t.scenarios.day}</TH>
                <TH>
                  {t.scenarios.channel} / {t.scenarios.template}
                </TH>
                <TH>{t.scenarios.condition}</TH>
                <TH>{t.scenarios.label}</TH>
                <TH>
                  <span className="sr-only">{t.common.actions}</span>
                </TH>
              </tr>
            </THead>
            <TBody>
              {steps.map((step, index) => (
                <StepRow
                  key={index}
                  step={step}
                  index={index}
                  count={steps.length}
                  templates={templates.data?.items ?? []}
                  readOnly={readOnly}
                  onChange={(patch) => update(index, patch)}
                  onMove={(delta) => move(index, delta)}
                  onRemove={() => setSteps((list) => list.filter((_, i) => i !== index))}
                />
              ))}
            </TBody>
          </Table>
          {readOnly ? null : (
            <div className="flex flex-wrap items-center gap-3 border-t border-border p-3">
              <Button onClick={submit} disabled={save.isPending}>
                {t.common.save}
              </Button>
              {saved ? (
                <span role="status" className="text-sm text-success">
                  {t.common.saved}
                </span>
              ) : null}
              {error ? (
                <p role="alert" className="text-sm text-destructive">
                  {error}
                </p>
              ) : null}
              <ActionError error={save.error ?? publish.error ?? duplicate.error} />
            </div>
          )}
        </Card>
        <Preview scenarioId={scenario.id} />
        {auth.can('editPortfolio') ? <Simulation scenarioId={scenario.id} /> : null}
      </div>
    </>
  );
}

export function ScenarioEditorPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const query = useApiQuery<Scenario>(`/v1/scenarios/${id}`);
  return (
    <QueryView query={query}>
      {(scenario) => <Editor key={`${scenario.id}-${scenario.version}`} scenario={scenario} />}
    </QueryView>
  );
}
